/* Input park: the drawer properties convert with the details-tab round (component-map.md). */
import { Box } from '@chakra-ui/react';
import {
  BuildingOffice2Icon,
  DocumentArrowDownIcon,
  DocumentCheckIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  GlobeEuropeAfricaIcon,
  ListBulletIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  Square3Stack3DIcon,
  SwatchIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Tag } from '@main/complykit/components/tag';
import { useUpdateOrganizationMutation } from '@main/graphql/mutations/UpdateOrganization.admin.generated';
import { useGetOrganizationQuery } from '@main/graphql/queries/GetOrganization.generated';
import { OrganizationTabKeys } from '@main/shared/utils';
import { Drawer, EditableAutoResizeTextarea, getVisibleTabs } from '@main/ui';
import { PlugIcon } from '@main/ui/icons';
import { useMemo } from 'react';

import { AgentsTab } from './agents-tab';
import { DetailsTab } from './details-tab';
import { EvidenceTab } from './evidence-tab';
import { FrameworksTab } from './frameworks-tab';
import { IntegrationsTab } from './integrations-tab';
import { InvitationsTab } from './invitations-tab';
import { PoliciesTab } from './policies-tab';
import { ProgramsTab } from './programs-tab';
import { QuestionnairesTab } from './questionnaires-tab';
import { ReviewsTab } from './reviews-tab';
import { RisksTab } from './risks-tab';
import { SubOrganizationsTab } from './sub-orgs-tab';
import { TasksTab } from './tasks-tab';
import { useUpdateOrganizationHandler } from './use-update-org-handler';
import { UsersTab } from './users-tab';
import { VendorsTab } from './vendors-tab';

const OrganizationDrawer = ({ orgId }: { orgId: string }) => {
  const { data, isLoading: isOrgLoading } = useGetOrganizationQuery({ orgId });

  const organization = data?.organizations_by_pk;
  const { tabs } = useOrgDrawerTabs(
    orgId,
    organization?.parent_organization_id,
    organization?.is_grove_enabled,
  );

  return (
    <Drawer.Layout isLoading={isOrgLoading} canView={true} isNotFound={!organization}>
      <Drawer.Toolbar />
      <Drawer.Tabs tabs={tabs}>
        <OrganizationDrawerHeader orgId={orgId} />
      </Drawer.Tabs>
    </Drawer.Layout>
  );
};

const OrganizationDrawerHeader = ({ orgId }: { orgId: string }) => {
  const [updateOrganization] = useUpdateOrganizationMutation();
  const updateOrgHanlder = useUpdateOrganizationHandler(orgId);

  const { data } = useGetOrganizationQuery({ orgId });
  const organization = data?.organizations_by_pk;

  if (!organization) {
    return null;
  }

  return (
    <>
      <Box mb={4}>
        <Tag color="purple">Organizations / {organization.id}</Tag>
      </Box>
      <EditableAutoResizeTextarea
        fontSize="xl"
        ml={-2}
        fontWeight="bold"
        defaultValue={organization.name}
        placeholder="Organization name"
        onSubmit={(value) => {
          if (value === organization.name) {
            return;
          }
          updateOrgHanlder(
            updateOrganization({
              organization_id: orgId,
              updatePayload: {
                name: value,
              },
            }),
          );
        }}
      />
    </>
  );
};

const useOrgDrawerTabs = (orgId: string, parentOrgId?: string | null, isGroveEnabled?: boolean) => {
  const tabs = useMemo(
    () =>
      getVisibleTabs<OrganizationTabKeys>({
        details: {
          label: 'Details',
          icon: ListBulletIcon,
          panel: <DetailsTab orgId={orgId} />,
          hideTabTitle: true,
        },
        programs: {
          label: 'Programs',
          icon: SwatchIcon,
          panel: <ProgramsTab orgId={orgId} />,
        },
        users: {
          label: 'Users',
          icon: UsersIcon,
          panel: <UsersTab orgId={orgId} />,
        },
        invitations: {
          label: 'Invitations',
          icon: EnvelopeIcon,
          panel: <InvitationsTab orgId={orgId} />,
        },
        sub_orgs: {
          label: 'Sub orgs',
          icon: UserGroupIcon,
          panel: <SubOrganizationsTab orgId={orgId} />,
          isHidden: !!parentOrgId,
        },
        integrations: {
          label: 'Integrations',
          icon: PlugIcon,
          panel: <IntegrationsTab orgId={orgId} />,
        },
        agents: {
          label: 'Agents',
          icon: SparklesIcon,
          panel: <AgentsTab orgId={orgId} />,
          isHidden: !isGroveEnabled,
        },
        evidence: {
          label: 'Evidence',
          icon: DocumentArrowDownIcon,
          panel: <EvidenceTab orgId={orgId} />,
        },
        frameworks: {
          label: 'Frameworks',
          icon: Square3Stack3DIcon,
          panel: <FrameworksTab orgId={orgId} />,
        },
        vendors: {
          label: 'Vendors',
          icon: BuildingOffice2Icon,
          panel: <VendorsTab orgId={orgId} />,
        },
        questionnaires: {
          label: 'Questionnaires',
          icon: QuestionMarkCircleIcon,
          panel: <QuestionnairesTab orgId={orgId} />,
        },
        policies: {
          label: 'Policies',
          icon: GlobeEuropeAfricaIcon,
          panel: <PoliciesTab orgId={orgId} />,
        },
        risks: {
          label: 'Risks',
          icon: BuildingOffice2Icon,
          panel: <RisksTab orgId={orgId} />,
        },
        tasks: {
          label: 'Tasks',
          icon: DocumentDuplicateIcon,
          panel: <TasksTab orgId={orgId} />,
        },
        reviews: {
          label: 'Reviews',
          icon: DocumentCheckIcon,
          panel: <ReviewsTab orgId={orgId} />,
        },
      }),
    [orgId, parentOrgId, isGroveEnabled],
  );

  return { tabs };
};

export default OrganizationDrawer;
